import React, { InputHTMLAttributes, forwardRef, useState } from 'react';
import './Input.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  showPasswordToggle?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      size = 'md',
      leftIcon,
      rightIcon,
      showPasswordToggle = false,
      type = 'text',
      className = '',
      id,
      required,
      disabled,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [inputType, setInputType] = useState(type);
    
    // Generate unique ID if not provided
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;
    
    // Handle password visibility toggle
    React.useEffect(() => {
      if (showPasswordToggle && type === 'password') {
        setInputType(showPassword ? 'text' : 'password');
      }
    }, [showPassword, showPasswordToggle, type]);

    const handlePasswordToggle = () => {
      setShowPassword(!showPassword);
    };

    const baseClasses = 'input-field';
    const sizeClass = `input-field--${size}`;
    const errorClass = error ? 'input-field--error' : '';
    const disabledClass = disabled ? 'input-field--disabled' : '';
    const iconClass = (leftIcon || rightIcon) ? 'input-field--with-icon' : '';
    
    const inputClasses = [
      'input-field__input',
      leftIcon ? 'input-field__input--with-left-icon' : '',
      (rightIcon || showPasswordToggle) ? 'input-field__input--with-right-icon' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const containerClasses = [
      baseClasses,
      sizeClass,
      errorClass,
      disabledClass,
      iconClass,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={containerClasses}>
        {label && (
          <label htmlFor={inputId} className="input-field__label">
            {label}
            {required && (
              <span className="input-field__required" aria-label="required">
                *
              </span>
            )}
          </label>
        )}
        
        <div className="input-field__wrapper">
          {leftIcon && (
            <span className="input-field__icon input-field__icon--left" aria-hidden="true">
              {leftIcon}
            </span>
          )}
          
          <input
            ref={ref}
            id={inputId}
            type={inputType}
            className={inputClasses}
            disabled={disabled}
            required={required}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={[
              error ? errorId : '',
              helperText ? helperId : '',
            ]
              .filter(Boolean)
              .join(' ') || undefined}
            {...props}
          />
          
          {showPasswordToggle && type === 'password' && (
            <button
              type="button"
              className="input-field__password-toggle"
              onClick={handlePasswordToggle}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={0}
            >
              {showPassword ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          )}
          
          {rightIcon && !showPasswordToggle && (
            <span className="input-field__icon input-field__icon--right" aria-hidden="true">
              {rightIcon}
            </span>
          )}
        </div>
        
        {error && (
          <div id={errorId} className="input-field__error" role="alert">
            {error}
          </div>
        )}
        
        {helperText && !error && (
          <div id={helperId} className="input-field__helper">
            {helperText}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';